// src/components/SeatLayout.jsx
import React from "react";
import PropTypes from "prop-types";
import { FaMale, FaFemale } from "react-icons/fa";

/* ---------- Matte premium palette (selected = blue) ---------- */
const PALETTE = {
  blue: "#4C6EF5",
  blueBorder: "#3F5ED8",
  blueHoverTint: "#EEF2FF",
  border: "#E5E7EB",
  borderSoft: "#EEF1F5",
  text: "#1A1A1A",
  textSubtle: "#6B7280",
  male: "#6356C8",
  maleBorder: "#5146B6",
  female: "#D95B84",
  femaleBorder: "#C94A74",
  locked: "#FEE2E2",
  lockedBorder: "#FCA5A5",
  lockedText: "#B91C1C",
  panelBg: "#FFFFFF",
  frontBadgeBg: "#F8FAFC",
};

/* ---------- Single Seat ---------- */
const Seat = ({
  seat,
  isBooked,
  isLocked,
  isSelected,
  gender,
  onClick,
  title,
}) => {
  const innerSeatClasses = isBooked
    ? gender === "F"
      ? "bg-[#D95B84] text-white border-[#C94A74] cursor-not-allowed shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]"
      : "bg-[#6356C8] text-white border-[#5146B6] cursor-not-allowed shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]"
    : isLocked
    ? "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5] cursor-not-allowed"
    : isSelected
    ? "bg-[#4C6EF5] text-white border-[#3F5ED8] shadow-[0_8px_20px_rgba(76,110,245,0.22)]"
    : "bg-white text-[#1A1A1A] border-[#E5E7EB] hover:bg-[#EEF2FF] hover:border-[#4C6EF5]";

  const disabled = isBooked || isLocked;

  const bottomBarColor = isBooked
    ? gender === "F"
      ? "bg-[#C94A74]"
      : "bg-[#5146B6]"
    : isLocked
    ? "bg-[#FCA5A5]"
    : isSelected
    ? "bg-[#3F5ED8]"
    : "bg-[#D1D5DB]";

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      title={title}
      aria-label={`Seat ${seat}`}
      aria-pressed={isSelected}
      disabled={disabled}
      className="
        relative group select-none
        w-12 h-12 sm:w-10 sm:h-10
        rounded-[14px] flex items-center justify-center
        transition-all duration-150
        active:scale-95 focus-visible:outline-none
        focus-visible:ring-2 focus-visible:ring-[#4C6EF5]/60
        disabled:opacity-95
      "
      style={{ WebkitTapHighlightColor: "transparent" }}
    >
      <span
        className={`
          absolute inset-[3px] sm:inset-[3px]
          border rounded-[11px]
          flex flex-col items-center justify-between
          font-semibold
          text-[11px] sm:text-xs
          ${innerSeatClasses}
        `}
      >
        <span className="flex-1 flex items-center justify-center leading-none">
          {isBooked ? (
            gender === "F" ? (
              <FaFemale className="text-[14px] sm:text-[13px]" />
            ) : (
              <FaMale className="text-[14px] sm:text-[13px]" />
            )
          ) : (
            seat
          )}
        </span>
        <span
          aria-hidden="true"
          className={`w-5 sm:w-4 h-[3px] rounded-t-sm self-center mb-[3px] ${bottomBarColor}`}
        />
      </span>
    </button>
  );
};

Seat.propTypes = {
  seat: PropTypes.string.isRequired,
  isBooked: PropTypes.bool.isRequired,
  isLocked: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  gender: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string,
};

/* ---------- Layout ---------- */
const SeatLayout = ({
  seatLayout,
  bookedSeats,
  selectedSeats,
  onSeatClick,
  bookedSeatGenders = {}, // ✅ default to safe empty object
}) => {
  const layoutAsStrings = Array.isArray(seatLayout)
    ? seatLayout.map(String)
    : [];

  const is49Seater = layoutAsStrings.length === 49;
  const is37Seater = layoutAsStrings.length === 37;

  const bookedSet = new Set(
    (Array.isArray(bookedSeats) ? bookedSeats : []).map(String)
  );
  const selectedSet = new Set(
    (Array.isArray(selectedSeats) ? selectedSeats : []).map(String)
  );

  const getAdjacentSeatInfo = (seatNumber, layoutGrid) => {
    for (const row of layoutGrid) {
      const idx = row.indexOf(seatNumber);
      if (idx !== -1) {
        if (idx > 0 && row[idx - 1] !== null) {
          const neighborSeat = String(row[idx - 1]);
          if (bookedSeatGenders?.[neighborSeat]) return bookedSeatGenders[neighborSeat];
        }
        if (idx < row.length - 1 && row[idx + 1] !== null) {
          const neighborSeat = String(row[idx + 1]);
          if (bookedSeatGenders?.[neighborSeat]) return bookedSeatGenders[neighborSeat];
        }
        return null;
      }
    }
    return null;
  };

  const getSeatStatus = (seat) => {
    const seatStr = String(seat);
    const isBooked = !!bookedSeatGenders?.[seatStr];
    const isLocked =
      bookedSet.has(seatStr) &&
      !bookedSeatGenders?.[seatStr] &&
      !selectedSet.has(seatStr);

    return {
      isBooked,
      isLocked,
      isSelected: selectedSet.has(seatStr),
      gender: isBooked ? bookedSeatGenders?.[seatStr] : null,
    };
  };

  const renderLayout = (layoutGrid) =>
    layoutGrid.map((row, rowIndex) => {
      const isLast49Row =
        is49Seater &&
        rowIndex === layoutGrid.length - 1 &&
        row.every((seat) => seat !== null);

      return (
        <div
          key={`row-${rowIndex}`}
          className={
            isLast49Row
              ? "flex justify-between items-center w-[256px] sm:w-[220px] mx-auto"
              : "flex justify-center items-center gap-x-2.5 sm:gap-x-2.5"
          }
        >
          {row.map((seatNumber, i) => {
            if (seatNumber === null) {
              return (
                <div
                  key={`aisle-${rowIndex}-${i}`}
                  className="w-[18px] sm:w-[20px] h-12 sm:h-10 rounded-xl"
                  style={{ opacity: 0 }}
                />
              );
            }

            const seat = String(seatNumber);

            if (!layoutAsStrings.includes(seat)) {
              return (
                <div
                  key={`placeholder-${rowIndex}-${i}`}
                  className="w-12 h-12 sm:w-10 sm:h-10"
                />
              );
            }

            const seatStatus = getSeatStatus(seat);
            let tooltipTitle = "";

            if (
              !seatStatus.isBooked &&
              !seatStatus.isLocked &&
              !seatStatus.isSelected
            ) {
              const adj = getAdjacentSeatInfo(seatNumber, layoutGrid);
              if (adj) {
                tooltipTitle = `Adjacent seat booked by a ${
                  adj === "F" ? "Female" : "Male"
                }`;
              }
            }

            return (
              <Seat
                key={seat}
                seat={seat}
                {...seatStatus}
                onClick={() => onSeatClick(seat)}
                title={tooltipTitle}
              />
            );
          })}
        </div>
      );
    });

  const getLayoutGrid = () => {
    if (is49Seater) {
      const grid = [];
      for (let i = 0; i < 11; i++) {
        grid.push([i * 4 + 1, i * 4 + 2, null, i * 4 + 3, i * 4 + 4]);
      }
      grid.push([45, 46, 47, 48, 49]);
      return grid;
    }

    if (is37Seater) {
      const base = [
        [1, 2, null, 3, 4],
        [5, 6, null, 7, 8],
        [9, 10, null, 11, 12],
        [13, 14, null, 15, 16],
        [17, 18, null, 19, 20],
        [21, 22, null, 23, 24],
        [25, 26, null, 27, 28],
        [29, 30, null, 31, 32],
      ];
      return [...base, [33, 34, 35, 36, 37]];
    }

    return [];
  };

  const layoutGrid = getLayoutGrid();

  return (
    <div
      className="p-3 sm:p-4 rounded-2xl overflow-x-auto shadow-[0_4px_18px_rgba(15,23,42,0.04)]"
      style={{
        background: PALETTE.panelBg,
        border: `1px solid ${PALETTE.borderSoft}`,
      }}
    >
      {/* Header */}
      <div className="relative flex justify-between items-center mb-3 sm:mb-4 px-2 sm:px-4">
        <span
          className="inline-flex items-center rounded-full px-2.5 py-1 font-bold text-[10px] sm:text-xs uppercase tracking-[0.18em]"
          style={{
            color: PALETTE.textSubtle,
            background: PALETTE.frontBadgeBg,
            border: `1px solid ${PALETTE.border}`,
          }}
        >
          Front
        </span>

        <div
          className="inline-flex items-center justify-center rounded-full px-2.5 py-1 text-[10px] sm:text-xs font-semibold"
          style={{
            color: PALETTE.textSubtle,
            background: PALETTE.frontBadgeBg,
            border: `1px solid ${PALETTE.border}`,
          }}
        >
          Driver
        </div>
      </div>

      {/* Seat grid */}
      <div className="space-y-2.5 sm:space-y-2.5 inline-block min-w-full">
        {layoutGrid.length > 0 ? (
          renderLayout(layoutGrid)
        ) : (
          <p className="text-center" style={{ color: PALETTE.blue }}>
            Unsupported seat layout.
          </p>
        )}
      </div>

      {/* Footer */}
      <div
        className="font-bold text-[11px] sm:text-sm uppercase tracking-wider text-center mt-3 sm:mt-4"
        style={{ color: PALETTE.textSubtle }}
      >
        Rear
      </div>
    </div>
  );
};

SeatLayout.propTypes = {
  seatLayout: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  bookedSeats: PropTypes.array.isRequired,
  selectedSeats: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  onSeatClick: PropTypes.func.isRequired,
  bookedSeatGenders: PropTypes.object,
};

SeatLayout.defaultProps = {
  bookedSeatGenders: {},
};

export default SeatLayout;
